import { throttle } from 'throttle-debounce';

export const lazyLoading = () => {
  const handleImages = () => {
    const images: HTMLImageElement[] = Array.from(
      document.querySelectorAll('img[loading="lazy"]')
    );
    if ('loading' in HTMLImageElement.prototype) {
      images.forEach((img) => {
        if (img.hasAttribute('data-src')) img.src = img.dataset.src;
        if (img.hasAttribute('data-srcset')) img.srcset = img.dataset.srcset;
      });
    } else {
      // Dynamically import the LazySizes library
      const script = document.createElement('script');
      images.forEach((img) => {
        img.classList.add('lazyload');
      });
      script.src = '/assets/scripts/lazysizes.min.js';
      if (!document.querySelector(`script[src="${script.src}"]`))
        document.body.appendChild(script);
    }
  };

  /**
   * Lazy load any `<video loading="lazy">` with `<source>` elements.
   * @example
   *   lazyVideos()
   */
  const handleVideos = () => {
    if ('IntersectionObserver' in window) {
      const videos: HTMLVideoElement[] = Array.from(
        document.querySelectorAll('video[loading="lazy"]')
      );

      videos.forEach((video) => {
        const lazyVideoObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              for (const source in entry.target.children) {
                const videoSource = <HTMLSourceElement>(
                  entry.target.children[source]
                );

                if (
                  typeof videoSource.tagName === 'string' &&
                  videoSource.tagName === 'SOURCE'
                ) {
                  videoSource.src = videoSource.dataset.src;
                }
              }

              video.load();
              if (!video.hasAttribute('autoplay')) {
                video.pause();
              } else {
                video.play();
              }

              video.classList.add('lazyloaded');
              lazyVideoObserver.unobserve(entry.target);
            }
          });
        });

        lazyVideoObserver.observe(video);
      });
    }
  };

  /**
   * The browser decides which file to load from a `srcset`. When selecting the image size, the browser is normally based on the size of the browser window. Instead, we recommend that the browser uses the size in which the image is actually displayed.
   */
  const handleImageSizes = () => {
    const items = Array.from(
      document.querySelectorAll('img[sizes]')
    ) as HTMLImageElement[];

    const update = throttle(300, () => {
      items.forEach((image) => {
        /** The size at which the {@link image} is actually displayed. In pixel. */
        const imageRect = image.getBoundingClientRect();
        let imageWidth = imageRect.width;

        if (image.dataset.sizesMode === 'cover') {
          const sizesRatio = Number(image.dataset.sizesRatio);
          const fallbackWidth = Number(image.getAttribute('width'));
          const fallbackHeight = Number(image.getAttribute('height'));
          const fallbackRatio = fallbackWidth / fallbackHeight;
          const imageRatio = Number.isFinite(sizesRatio)
            ? sizesRatio
            : fallbackRatio;

          if (Number.isFinite(imageRatio) && imageRatio > 0) {
            imageWidth = Math.max(imageWidth, imageRect.height * imageRatio);
          }
        }

        image.sizes = imageWidth > 0 ? Math.ceil(imageWidth) + 'px' : 'auto';
      });
    });

    window.addEventListener('resize', update);
    update();
  };

  handleImageSizes();
  handleImages();
  handleVideos();
};
